import * as React from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { runOnJS, useAnimatedProps, useAnimatedReaction, useAnimatedStyle } from 'react-native-reanimated';
import useStyles from 'hooks/useStyles';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import IconDown from 'design-system/icons/IconDown';
import Text, { styles as textStyles } from 'design-system/Text';
import { AnimatedTextInput } from 'design-system/AnimatedComponents';
import useColors from 'hooks/useColors';
import createStyleSheets from 'utils/createStyleSheets';
import { useCurrentPrice, usePriceChange } from '../hooks/selectors';
import { getPriceChangeViewValues, getColorKeyFromPriceTrendWorklet } from '../utils/portfolio';
import { isWeb } from '../../../constants';
const arrowViewWidth = rem(12);
const StockInfoPriceChangeView = ({ assetId, priceTrend, selectedPeriod, selectedInfoObj, priceChangeValue, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const priceChangeInfo = usePriceChange(assetId);
    const currentPrice = useCurrentPrice(assetId);
    const priceChange = React.useMemo(() => getPriceChangeViewValues(priceChangeInfo?.value, (priceChangeInfo?.percent || 0) * 100, currentPrice?.currency), [currentPrice?.currency, priceChangeInfo?.percent, priceChangeInfo?.value]);
    // On web the text input keeps a fixed width and ignores the animated colour, so
    // the selected point is mirrored into state and drawn as text instead
    const [webSelected, setWebSelected] = React.useState();
    useAnimatedReaction(() => isWeb && selectedInfoObj.value
        ? { text: selectedInfoObj.value.priceChangeValue || '', trend: selectedInfoObj.value.priceTrend }
        : undefined, (current, previous) => {
        if (current?.text !== previous?.text || current?.trend !== previous?.trend) {
            runOnJS(setWebSelected)(current);
        }
    }, [selectedInfoObj]);
    const priceNodeProps = useAnimatedProps(() => {
        if (selectedInfoObj.value) {
            return {
                text: selectedInfoObj.value.priceChangeValue || '',
                color: colors.text[getColorKeyFromPriceTrendWorklet(selectedInfoObj.value.priceTrend)],
            };
        }
        if (selectedPeriod === '1D') {
            return {
                text: priceChange.priceChangeValue || '',
                color: colors.text[getColorKeyFromPriceTrendWorklet(priceChange.priceTrend)],
            };
        }
        return {
            text: priceChangeValue || '',
            color: colors.text[getColorKeyFromPriceTrendWorklet(priceTrend)],
        };
    }, [
        selectedInfoObj,
        selectedPeriod,
        priceChangeValue,
        colors.text,
        priceTrend,
        priceChange.priceChangeValue,
        priceChange.priceTrend,
    ]);
    const arrowViewStyles = useAnimatedStyle(() => {
        if (selectedInfoObj.value) {
            return {
                width: selectedInfoObj.value.priceTrend === undefined ? 0 : arrowViewWidth,
            };
        }
        if (selectedPeriod === '1D') {
            return {
                width: priceChange.priceTrend === undefined ? 0 : arrowViewWidth,
            };
        }
        return {
            width: priceTrend === undefined ? 0 : arrowViewWidth,
        };
    }, [priceChange.priceTrend, priceTrend, selectedInfoObj, selectedPeriod]);
    const upArrowAnimatedStyles = useAnimatedStyle(() => {
        if (selectedInfoObj.value) {
            return {
                opacity: selectedInfoObj.value.priceTrend === 1 ? 1 : 0,
            };
        }
        if (selectedPeriod === '1D') {
            return {
                opacity: priceChange.priceTrend === 1 ? 1 : 0,
            };
        }
        return {
            opacity: priceTrend === 1 ? 1 : 0,
        };
    }, [priceChange.priceTrend, priceTrend, selectedInfoObj, selectedPeriod]);
    const downArrowAnimatedStyles = useAnimatedStyle(() => {
        if (selectedInfoObj.value) {
            return {
                opacity: selectedInfoObj.value.priceTrend === -1 ? 1 : 0,
            };
        }
        if (selectedPeriod === '1D') {
            return {
                opacity: priceChange.priceTrend === -1 ? 1 : 0,
            };
        }
        return {
            opacity: priceTrend === -1 ? 1 : 0,
        };
    }, [priceChange.priceTrend, priceTrend, selectedInfoObj, selectedPeriod]);
    const neutralArrowAnimatedStyles = useAnimatedStyle(() => {
        if (selectedInfoObj.value) {
            return {
                opacity: selectedInfoObj.value.priceTrend === 0 ? 1 : 0,
            };
        }
        if (selectedPeriod === '1D') {
            return {
                opacity: priceChange.priceTrend === 0 ? 1 : 0,
            };
        }
        return {
            opacity: priceTrend === 0 ? 1 : 0,
        };
    }, [priceChange.priceTrend, priceTrend, selectedInfoObj, selectedPeriod]);
    if (isWeb) {
        let webText = priceChangeValue || '';
        let webTrend = priceTrend;
        if (webSelected) {
            webText = webSelected.text;
            webTrend = webSelected.trend;
        }
        else if (selectedPeriod === '1D') {
            webText = priceChange.priceChangeValue || '';
            webTrend = priceChange.priceTrend;
        }
        return (<View style={[styles.row, styles.webRow]}>
        {webTrend !== undefined && (<View style={[styles.arrowView, styles.webArrowView]}>
            {webTrend === 1 && (<View style={styles.upIcon}>
                <IconDown color={colors.text.positive}/>
              </View>)}
            {webTrend === -1 && (<View style={styles.iconWidth}>
                <IconDown color={colors.text.negative}/>
              </View>)}
            {webTrend === 0 && <View style={styles.neutralIndicator}/>}
          </View>)}
        <Text NumbersThin-14 numberOfLines={1} color={colors.text[getColorKeyFromPriceTrendWorklet(webTrend)]}>
          {webText}
        </Text>
        <Spacer w={8}/>
      </View>);
    }
    return (<View style={styles.row}>
      <Animated.View style={[styles.arrowView, arrowViewStyles]}>
        <Animated.View style={[styles.icon, upArrowAnimatedStyles]}>
          <View style={styles.upIcon}>
            <IconDown color={colors.text.positive}/>
          </View>
        </Animated.View>
        <Animated.View style={[styles.icon, downArrowAnimatedStyles]}>
          <View style={styles.iconWidth}>
            <IconDown color={colors.text.negative}/>
          </View>
        </Animated.View>
        <Animated.View style={[styles.icon, neutralArrowAnimatedStyles]}>
          <View style={styles.iconWidth}>
            <View style={styles.neutralIndicator}/>
          </View>
        </Animated.View>
      </Animated.View>
      <AnimatedTextInput editable={false} numberOfLines={1} style={styles.priceNode} allowFontScaling={false} animatedProps={priceNodeProps} defaultValue={(selectedPeriod === '1D' ? priceChange.priceChangeValue : priceChangeValue) || ''}/>
      <Spacer w={8}/>
    </View>);
};
export default React.memo(StockInfoPriceChangeView);
const styleSet = createStyleSheets((colors) => ({
    row: {
        flexDirection: 'row',
    },
    arrowView: {
        width: rem(16),
    },
    // Centres the arrow on the text rather than its top
    webRow: {
        alignItems: 'center',
    },
    webArrowView: {
        width: arrowViewWidth,
        justifyContent: 'center',
    },
    priceNode: {
        margin: 0,
        padding: 0,
        ...textStyles.NumbersThin_14,
    },
    icon: {
        ...StyleSheet.absoluteFillObject,
        justifyContent: 'center',
    },
    neutralIndicator: {
        width: rem(6),
        height: rem(6),
        borderRadius: rem(6),
        backgroundColor: colors.text.secondary,
    },
    upIcon: {
        width: rem(7),
        transform: [{ rotate: '180deg' }],
    },
    iconWidth: {
        width: rem(7),
    },
}));
